<template>
  <div class="w-full h-full">
    <FlashMessage v-if="this.messages.text != ''" :message="this.messages" />

    <!-- Feedback Form - Now positioned above score card when conditions are met -->
    <div
      v-if="
        quizSubmitted &&
        showFeedback &&
        !hasUserGivenFeedback &&
        quizPassed === true
      "
      class="w-full flex justify-center mb-6"
    >
      <div
        class="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl shadow-lg w-full max-w-2xl border border-blue-100"
      >
        <div class="flex items-start gap-3 mb-4">
          <div class="bg-blues rounded-full p-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 text-white"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z"
              />
              <path
                d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z"
              />
            </svg>
          </div>
          <div>
            <h3 class="text-lg font-semibold text-gray-900">
              Congratulations! 🎉
            </h3>
            <p class="text-sm text-gray-600">
              You've passed the quiz. Please share your feedback to help us
              improve.
            </p>
          </div>
        </div>

        <form
          ref="submitfeedback"
          @submit.prevent="scheduleFeedback"
          class="space-y-4"
        >
          <div class="space-y-2">
            <label
              for="description"
              class="block text-sm font-medium text-gray-700"
            >
              Your Feedback 
            </label>
            <textarea
              id="description"
              rows="4"
              minlength="10"
              
              placeholder="Tell us what you thought about this quiz..."
              class="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm transition-all resize-none"
            ></textarea>
            <p class="text-xs text-gray-500">Minimum 10 characters</p>
          </div>

          <div class="space-y-2">
            <label class="block text-sm font-medium text-gray-700">
              Rating <span class="text-red-500">*</span>
            </label>
            <div class="flex items-center gap-2">
              <template v-for="i in 5" :key="i">
                <button
                  type="button"
                  @click="setRating(i)"
                  class="focus:outline-none transform transition-transform hover:scale-110"
                >
                  <svg
                    :class="[
                      'w-7 h-7 transition-colors duration-200',
                      i <= selectedRating
                        ? 'text-yellow-400'
                        : 'text-gray-300 hover:text-yellow-200',
                    ]"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.975a1 1 0 00.95.69h4.178c.969 0 1.371 1.24.588 1.81l-3.39 2.46a1 1 0 00-.364 1.118l1.287 3.976c.3.921-.755 1.688-1.538 1.118l-3.39-2.46a1 1 0 00-1.176 0l-3.39 2.46c-.783.57-1.838-.197-1.538-1.118l1.287-3.976a1 1 0 00-.364-1.118L2.05 9.402c-.783-.57-.38-1.81.588-1.81h4.178a1 1 0 00.95-.69l1.286-3.975z"
                    />
                  </svg>
                </button>
              </template>
              <span
                v-if="selectedRating > 0"
                class="ml-2 text-sm text-gray-600"
              >
                {{
                  ["Poor", "Fair", "Good", "Very Good", "Excellent"][
                    selectedRating - 1
                  ]
                }}
              </span>
            </div>
          </div>

          <div
            class="flex items-center justify-end gap-3 pt-4 border-t border-gray-100"
          >
            <button
              type="button"
              @click="redirectAfterQuiz"
              class="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
            >
              Skip for now
            </button>
            <button
              type="submit"
              :disabled="disable_submit || selectedRating === 0"
              :class="[
                'px-6 py-2 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all',
                disable_submit || selectedRating === 0
                  ? 'bg-gray-400 cursor-not-allowed'
                  : 'bg-blues hover:bg-hover focus:ring-blue-500 shadow-lg hover:shadow-xl',
              ]"
            >
              <span class="flex items-center gap-2">
                <svg
                  v-if="!disable_submit"
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clip-rule="evenodd"
                  />
                </svg>
                <svg
                  v-else
                  class="animate-spin h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  ></circle>
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                {{ disable_submit ? "Submitting..." : "Submit Feedback" }}
              </span>
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Score Display Section - Now below feedback form -->
    <div class="flex justify-end mb-20" v-if="this.duration?.quiz?.persentage">
      <div
        class="bg-white rounded-lg shadow-md p-5 max-w-xs w-full border-l-4"
        :class="{
          'border-green-500': quizPassed === true,
          'border-red-500': quizPassed === false,
          'border-yellow-500': quizPassed === undefined,
        }"
      >
        <div class="flex items-start">
          <div class="flex-1">
            <!-- Score Display -->
            <div class="mb-3 flex items-center">
              <p class="text-sm text-gray-600">
                Max-
                <span class="text-sm font-normal text-gray-500">
                  {{ this.duration?.quiz?.persentage_score }}%
                </span>
              </p>
              <span class="px-4">-</span>
              <p
                class="text-2xl font-bold"
                :class="{
                  'text-green-600': quizPassed === true,
                  'text-red-600': quizPassed === false,
                  'text-gray-800': quizPassed === undefined,
                }"
              >
                {{ this.tryquizs?.persentage_scored || 0 }}%
              </p>

              <!-- Pass/Fail Badge -->
              <div v-if="quizPassed !== undefined" class="px-2">
                <span
                  class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                  :class="{
                    'bg-green-100 text-green-800': quizPassed === true,
                    'bg-red-100 text-red-800': quizPassed === false,
                  }"
                >
                  <span v-if="quizPassed === true">✓ Passed</span>
                  <span v-if="quizPassed === false">✗ Failed</span>
                </span>
              </div>
            </div>

            <!-- Action Buttons - Show only when failed -->
            <div
              class="flex gap-2"
              v-if="quizSubmitted && quizPassed === false"
            >
              <!-- Retry Button -->
              <button
                @click="retryQuiz"
                class="flex-1 px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white text-sm font-medium rounded-md transition-colors duration-200 shadow-sm hover:shadow focus:ring-2 focus:ring-yellow-300 focus:outline-none"
              >
                <span class="flex items-center justify-center gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  Try Quiz Again
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div>
      <div
        v-if="!quizSubmitted && !showFeedback"
        class="w-full max-w-7xl mx-auto flex flex-col gap-4 h-full"
      >
        <div
          class="max-w-4xl w-full rounded-3xl mx-auto flex items-center justify-center"
        >
          <div class="w-full h-full flex flex-col items-center justify-center">
            <div class="h-full w-full -mt-5">
              <div
                v-if="questions.length > 0"
                class="w-full h-full flex flex-col justify-center items-center"
              >
                <div class="w-full h-auto flex flex-col justify-center -mt-10">
                  <div class="flex justify-between gap-3">
                    <div></div>
                    <div></div>
                    <div></div>
                    <div></div>
                    <div
                      class="text-center text-lg font-medium px-3"
                      style="color: #01b0f9"
                    >
                      Question {{ currentQuestion + 1 }} /
                      {{ questions.length }}
                    </div>
                    <div></div>
                    <div></div>
                    <div class="flex items-center gap-1 text-lg font-bold">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        stroke-width="1.5"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M12 6v6l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      {{ formattedTime }}
                    </div>
                  </div>

                  <div
                    class="border-red-400 bg-indigo-900 h-0.5 my-2"
                    :max="30"
                    :value="countDown"
                  ></div>
                  <div v-if="questions[currentQuestion].image">
                    <img
                      class="w-full h-full"
                      :src="questions[currentQuestion].image"
                    />
                  </div>
                  <div class="text-center py-4 flex justify-center">
                    <span class="">{{ currentQuestion + 1 }}.</span>
                    <p v-html="questions[currentQuestion].question_text"></p>
                  </div>

                  <div
                    class="flex flex-wrap justify-center gap-4 w-full"
                    v-if="questions[currentQuestion].choice_type == 'choice'"
                  >
                    <button
                      style="border: 1px solid #01b0f9"
                      class="border rounded py-3 px-4 w-5/12 relative text-sm hover:bg-blue-900 hover:text-white"
                      :class="[
                        questions[currentQuestion].ansclk == option.id
                          ? 'bg-blue-900 text-white border-gray-400'
                          : '',
                      ]"
                      :key="index"
                      v-for="(option, index) in questions[currentQuestion]
                        .coursequizanswer_set"
                      @click="handleAnswerClick(option.is_correct, option.id)"
                    >
                      {{ option.answer_text }}
                    </button>
                  </div>

                  <div
                    class="flex flex-wrap justify-center gap-4 w-full"
                    v-if="questions[currentQuestion].choice_type == 'truefalse'"
                  >
                    <button
                      style="border: 1px solid #01b0f9"
                      class="border rounded py-3 px-4 w-5/12 relative text-sm hover:bg-blue-900 hover:text-white"
                      :class="[
                        questions[currentQuestion].ansclk == option.id
                          ? 'bg-blue-900 text-white border-gray-400'
                          : '',
                      ]"
                      :key="index"
                      v-for="(option, index) in questions[currentQuestion]
                        .coursequizanswer_set"
                      @click="handleAnswerClick(option.is_correct, option.id)"
                    >
                      {{ option.answer_text }}
                    </button>
                  </div>

                  <div
                    class="flex flex-col flex-wrap justify-center gap-4 w-full"
                    v-if="
                      questions[currentQuestion].choice_type ==
                      'matchthefollowing'
                    "
                  >
                    <p class="text-center text-sm">
                      Drag and drop the match the options
                    </p>

                    <div class="grid grid-cols-2 gap-10">
                      <div class="flex flex-col gap-2">
                        <div
                          v-for="left_items in questions[currentQuestion]
                            .coursequizanswer_set.left_items"
                          :key="left_items"
                          class="text-sm px-5 py-2 bg-gray-100"
                        >
                          Question: {{ left_items.value }}
                        </div>
                      </div>
                      <draggable
                        :list="
                          questions[currentQuestion].coursequizanswer_set
                            .right_items
                        "
                        class="dragArea list-group w-full flex flex-col gap-2"
                      >
                        <div
                          v-for="right_items in questions[currentQuestion]
                            .coursequizanswer_set.right_items"
                          :key="right_items"
                          class="text-sm px-5 py-2 bg-gray-200 cursor-pointer"
                        >
                          :: {{ right_items.value }}
                        </div>
                      </draggable>
                    </div>
                    <br />
                  </div>

                  <div
                    class="flex flex-wrap justify-center gap-4 w-full"
                    v-if="
                      questions[currentQuestion].choice_type ==
                      'shortdescription'
                    "
                  >
                    <textarea
                      class="w-full border rounded-lg p-2"
                      rows="5"
                      required
                      v-model="questions[currentQuestion].short_description"
                    >
                    </textarea>
                  </div>

                  <div
                    class="flex flex-wrap justify-center gap-4 w-full"
                    v-if="
                      questions[currentQuestion].choice_type == 'description'
                    "
                  >
                    <textarea
                      class="w-full border rounded-lg p-2"
                      rows="5"
                      required
                      v-model="questions[currentQuestion].description"
                    >
                    </textarea>
                  </div>

                  <div
                    class="flex flex-wrap justify-center gap-4 w-full"
                    v-if="questions[currentQuestion].choice_type == 'coding'"
                  >
                    <div
                      class="w-full bg-gray-900 text-white rounded-lg overflow-hidden"
                    >
                      <!-- Header -->
                      <div
                        class="flex justify-between items-center px-5 py-3 bg-gray-800 border-b border-gray-700"
                      >
                        <div class="flex items-center gap-4">
                          <h3 class="text-sm font-semibold">Code Editor</h3>
                          <select
                            v-model="selectedLanguage"
                            class="px-3 py-1.5 border border-gray-700 rounded bg-gray-700 text-white text-xs cursor-pointer outline-none transition-all hover:bg-gray-600"
                            disabled
                          >
                            <option
                              v-for="lang in languages"
                              :key="lang"
                              :value="lang"
                            >
                              {{ lang.charAt(0).toUpperCase() + lang.slice(1) }}
                            </option>
                          </select>
                        </div>
                        <div class="flex items-center gap-3">
                          <button
                            @click="runCode"
                            class="px-4 py-1.5 rounded text-xs font-semibold transition-all outline-none"
                            :class="
                              isRunning
                                ? 'bg-gray-600 cursor-not-allowed opacity-60'
                                : 'bg-blues hover:bg-blue-500'
                            "
                            :disabled="isRunning"
                          >
                            <span v-if="!isRunning">▶ Run Code</span>
                            <span v-else>⏳ Running...</span>
                          </button>
                          <button
                            @click="clearCode"
                            class="px-4 py-1.5 rounded text-xs font-semibold bg-gray-700 hover:bg-gray-600 transition-all outline-none"
                          >
                            🗑️ Clear
                          </button>
                        </div>
                      </div>

                      <div class="flex" style="height: 400px">
                        <div
                          class="flex-1 flex flex-col overflow-hidden border-r border-gray-700"
                        >
                          <div
                            class="flex justify-between items-center px-4 py-2 bg-gray-800 border-b border-gray-700 text-xs font-semibold"
                          >
                            <span>📝 Editor</span>
                          </div>

                          <MonacoEditor
                            :value="code"
                            :language="selectedLanguage"
                            height="100%"
                            theme="vs-dark"
                            :options="editorOptions"
                            class="flex-1"
                            @change="handleCodeChange"
                          />
                        </div>

                        <!-- Right Side - Output -->
                        <div class="flex-1 flex flex-col overflow-hidden">
                          <div
                            class="flex justify-between items-center px-4 py-2 bg-gray-800 border-b border-gray-700 text-xs font-semibold"
                          >
                            <span>📤 Output</span>
                          </div>
                          <div class="flex-1 p-4 overflow-y-auto bg-gray-900">
                            <div
                              v-if="output"
                              class="font-mono text-xs whitespace-pre-wrap leading-relaxed"
                              :class="
                                isError ? 'text-red-400' : 'text-gray-300'
                              "
                            >
                              {{ output }}
                            </div>
                            <div
                              v-else
                              class="text-gray-500 italic text-center mt-12 text-xs"
                            >
                              Click "Run Code" to see output here...
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div
                v-else
                class="w-full h-32 flex flex-col justify-center items-center max-h-[358px] text-xl"
              >
                {{ this.message }}
              </div>

              <div
                v-if="questions.length > 0"
                class="max-w-4xl bg-gray-200 overflow-x-auto"
                style="min-width: 360px"
              ></div>

              <div
                v-if="questions.length > 0"
                class="mt-10 pb-5 flex gap-10 justify-center"
              >
                <button
                  v-if="currentQuestion > 0"
                  :disabled="disable_submit == true"
                  :class="
                    this.disable_submit == true
                      ? 'bg-gray-200 text-black cursor-not-allowed w-[160px] py-2'
                      : 'gradient-btn hover:bg-hover cursor-pointer'
                  "
                  @click="pvsQt"
                >
                  Previous
                </button>
                <button
                  v-if="currentQuestion + 1 < this.questions.length"
                  class="gradient-btn"
                  @click="nxtQt"
                >
                  Next
                </button>
                <div class="flex gap-3 items-center">
                  <button
                    v-if="currentQuestion + 1 == this.questions.length"
                    @click="ckans"
                    :disabled="disable_submit == true"
                    :class="
                      this.disable_submit == true
                        ? 'bg-gray-200 text-black cursor-not-allowed w-[160px] py-2'
                        : 'gradient-btn hover:bg-hover cursor-pointer'
                    "
                  >
                    Submit
                  </button>
                  <img
                    v-if="disable_submit"
                    src="@/assets/loading.gif"
                    alt="loading"
                    class="w-6 h-6 object-contain"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Old Feedback Form removed - now positioned at the top -->
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { mapActions, mapGetters } from "vuex";
import FlashMessage from "@/components/FlashMessage.vue";
import { VueDraggableNext } from "vue-draggable-next";
import MonacoEditor from "monaco-editor-vue3";

export default {
  name: "testMCqview",
  components: {
    FlashMessage,
    draggable: VueDraggableNext,
    MonacoEditor,
  },
  data() {
    return {
      hasUserGivenFeedback: false,
      feedbackSubmitted: false,
      published: null,
      selectedRating: 0,
      quizSubmitted: false,
      showFeedback: false,
      currentQuestion: 0,
      countDown: 840,
      showScore: false,
      questions: [],
      tst: false,
      score: 0,
      is_completed: false,
      is_submit: false,
      nextquz: 0,
      message: "",
      mcqset: "",
      dataval: null,
      answeredData: "",
      currentAnsweredQuestion: 0,
      messages: {
        text: "",
        type: "null",
      },
      loading: false,
      disable_submit: false,
      codingAnswers: {},
      code: "",
      output: "",
      isError: false,
      isRunning: false,
      selectedLanguage: "python",
      languages: ["python", "c", "cpp", "java", "Javascript"],
      editorOptions: {
        theme: "vs-dark",
        fontSize: 13,
        automaticLayout: true,
        minimap: { enabled: false },
        scrollBeyondLastLine: false,
        lineNumbers: "on",
        roundedSelection: false,
        scrollbar: {
          vertical: "visible",
          horizontal: "visible",
        },
      },
      duration: "",
      timeLeft: 0,
      timerInterval: null,
      endTime: null,
      isAutoSubmit: false,
      quizPassed: undefined,
      quizInterval: null,
      tryquizs: [],
    };
  },
  props: ["quiz_id", "taker_id", "course_id","feedback"],

  computed: {
    ...mapGetters([
      "isAuthenticated",
      "userInfo",
      "profileInfo",
      "cartInfo",
      "courseTrackerInfo",
      "subscriptionInfo",
    ]),
    formattedTime() {
      if (!this.timeLeft || isNaN(this.timeLeft)) return "00:00";
      const minutes = Math.floor(this.timeLeft / 60);
      const seconds = this.timeLeft % 60;
      return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
        2,
        "0",
      )}`;
    },
  },

  async created() {
    this.mcqCall();
    this.tst = false;
    this.countDownTimer();

    let token = this.userInfo.token_key;

    if (this.taker_id != null) {
      axios
        .get(
          `${process.env.VUE_APP_API}/quiztaker__quizzes_pk/${this.quiz_id}/?Key=${token}&user__id=${this.taker_id}`,
        )
        .then((response) => {
          if (response.data.status == 200) {
            this.dataval = response.data.data;
          }
          console.log("response:: test", response.data.message);
        });
    }
  },

  mounted() {
    history.pushState(null, null, location.href);
    window.onpopstate = function () {
      history.go(1);
    };
    this.fetchFeedbackStatus();
    this.$watch(
      () => this.questions,
      (newVal) => {
        if (newVal && newVal.length > 0) {
          this.loadCodingQuestion();
        }
      },
      { deep: true, immediate: true },
    );
  },

  beforeUnmount() {
    window.onpopstate = null;
    this.cleanupQuizTimer();
  },

  beforeRouteLeave(to, from, next) {
    this.cleanupQuizTimer();
    next();
  },

  watch: {
    currentQuestion(newVal) {
      if (
        this.questions[newVal] &&
        this.questions[newVal].choice_type === "coding"
      ) {
        this.loadCodingQuestion();
      }
    },

    duration: {
      handler(newVal) {
        if (newVal?.quiz?.timer) {
          this.startQuizTimer();
        }
      },
      deep: true,
      immediate: true,
    },
  },

  methods: {
 async handlePostQuizFlow() {
  // Wait a moment for data to settle
  await this.$nextTick();

  const userScore = this.tryquizs?.persentage_scored || 0;
  const requiredScore = this.tryquizs?.persentage_need || 0;
  const hasPercentage = this.tryquizs?.persentage || false;

  console.log("Score Comparison:", {
    userScore,
    requiredScore,
    hasPercentage,
    tryquizs: this.tryquizs,
  });

  // Set quizPassed based on the response
  if (hasPercentage) {
    this.quizPassed = userScore >= requiredScore;
  } else {
    this.quizPassed = true;
  }

  // If feedback prop is FALSE - only emit, NO popup whatsoever
  if (this.feedback === false) {
    this.showFeedback = false;
    this.$emit("pushLessonComplete");
    return;
  }

  // If feedback prop is TRUE - show feedback form
  if (this.feedback === true) {
    if (this.quizPassed === true) {
      await this.fetchFeedbackStatus();
      
      if (this.hasUserGivenFeedback === true) {
        this.redirectAfterQuiz();
        return;
      }
      
      // Show feedback form for passed quizzes when feedback=true
      this.showFeedback = true;
    } else {
      // Failed case - show error message
      this.showFeedback = false;
      this.hasUserGivenFeedback = false;
      this.messages = {
        text: `You scored ${userScore}%. Required: ${requiredScore}%. Please try again.`,
        type: "error",
      };
    }
    return;
  }

  // Fallback for undefined feedback prop (original behavior)
  if (this.quizPassed === true) {
    await this.fetchFeedbackStatus();

    if (this.hasUserGivenFeedback === true) {
      this.redirectAfterQuiz();
      return;
    }

    this.showFeedback = true;
    this.$emit("pushLessonComplete");
  } else {
    this.showFeedback = false;
    this.hasUserGivenFeedback = false;

    this.messages = {
      text: `You scored ${userScore}%. Required: ${requiredScore}%. Please try again.`,
      type: "error",
    };
  }
},

    async fetchFeedbackStatus() {
      const response = await axios.get(
        `${process.env.VUE_APP_API}/lesson_quizzes_detail/${this.course_id}/?Key=${this.userInfo.token_key}&id=${this.quiz_id}`,
      );
      if (Array.isArray(response.data?.data) && response.data.data.length > 0) {
        this.published = response.data.data;
        this.hasUserGivenFeedback =
          !!this.published[0]?.quiz?.quiz_feedback?.user_feedback;
      } else {
        this.hasUserGivenFeedback = false;
      }
    },

   redirectAfterQuiz() {
  // Just emit and close feedback
  this.showFeedback = false;
  this.$emit("pushLessonComplete");
},

    async scheduleFeedback() {
      if (this.disable_submit) return;
      this.disable_submit = true;
      try {
        const bodyVal = {
          quize: this.quiz_id,
          text: document.getElementById("description")?.value || "",
          rating: this.selectedRating,
        };

        const response = await axios.post(
          `${process.env.VUE_APP_API}/quiz-feedback/create/?Key=${this.userInfo.token_key}`,
          bodyVal,
        );

        if (response.data.status === 201) {
          this.hasUserGivenFeedback = true;
          this.showFeedback = false;

          this.messages = {
            text: "Feedback submitted successfully",
            type: "success",
          };

          if (this.published?.quiz?.quiz_feedback) {
            this.published.quiz.quiz_feedback.user_feedback = bodyVal.text;
          }

          setTimeout(() => {
            this.messages = { text: "", type: "" };
              // window.location.reload();
        this.$emit("pushLessonComplete");
          }, 1000);
        }
      } catch (error) {
        console.error(error);
        this.messages = {
          text: "Feedback submission failed",
          type: "error",
        };
      } finally {
        this.disable_submit = false;
      }
    },

    setRating(rating) {
      this.selectedRating = rating;
    },

    autoSubmitFromOutside() {
      if (this.disable_submit) return;
      this.ckans();
    },

    startQuizTimer() {
      if (!this.duration?.quiz?.timer) return;

      const savedTime = localStorage.getItem("quiz_time_left");

      this.timeLeft = savedTime
        ? parseInt(savedTime)
        : this.duration.quiz.timer * 60;

      if (this.quizInterval) clearInterval(this.quizInterval);

      this.quizInterval = setInterval(() => {
        if (this.timeLeft > 0) {
          this.timeLeft--;
          localStorage.setItem("quiz_time_left", this.timeLeft);

          if (this.timeLeft <= 0) {
            console.log("Time's up! Auto-submitting quiz...");
            clearInterval(this.quizInterval);
            this.quizInterval = null;
            this.timeLeft = 0;
            localStorage.removeItem("quiz_time_left");

            if (!this.quizSubmitted && !this.disable_submit) {
              this.isAutoSubmit = true;
              this.ckans();
            }
          }
        } else {
          clearInterval(this.quizInterval);
          this.quizInterval = null;
        }
      }, 1000);
    },

    clearCode() {
      this.code = this.questions[this.currentQuestion]?.starter_code || "";
      this.output = "";
      this.isError = false;
    },

    handleCodeChange(newCode) {
      this.code = newCode;
      const questionId = this.questions[this.currentQuestion].id;
      if (!this.codingAnswers[questionId]) {
        this.codingAnswers[questionId] = {};
      }
      this.codingAnswers[questionId].coding = newCode;
    },

    loadCodingQuestion() {
      const currentQ = this.questions[this.currentQuestion];
      if (currentQ?.choice_type === "coding") {
        if (currentQ.programming_language) {
          this.selectedLanguage = currentQ.programming_language.toLowerCase();
        }

        const questionId = currentQ.id;
        if (
          this.codingAnswers[questionId] &&
          this.codingAnswers[questionId].coding
        ) {
          this.code = this.codingAnswers[questionId].coding;
          this.output = this.codingAnswers[questionId].output || "";
        } else {
          this.code = currentQ.starter_code || "";
          this.output = "";
        }

        this.isError = false;
      }
    },

    async runCode() {
      if (!this.code?.trim()) {
        this.output = "Error: No code to execute";
        this.isError = true;
        return;
      }

      if (!this.selectedLanguage) {
        this.output = "Error: No programming language selected";
        this.isError = true;
        return;
      }

      this.isRunning = true;
      this.isError = false;
      this.output = "Running code...";

      try {
        const { data } = await axios.post(
          `${process.env.VUE_APP_API}/compiler/`,
          { code: this.code, language: this.selectedLanguage },
        );

        if (data.status === 400 || data.errors) {
          this.output = data.errors || "Compilation/Runtime Error";
          this.isError = true;
        } else if (data.output) {
          this.output = Array.isArray(data.output)
            ? data.output.length
              ? data.output.join("\n")
              : "Code executed successfully with no output"
            : data.output;
          this.isError = false;
        } else {
          this.output = "Code executed successfully with no output";
          this.isError = false;
        }

        const qid = this.questions[this.currentQuestion].id;
        this.codingAnswers[qid] = {
          output: this.output,
          coding: this.code,
        };
      } catch (error) {
        console.error("Error running code:", error);
        this.isError = true;

        const res = error.response?.data;

        if (res?.errors) this.output = res.errors;
        else if (res?.error) this.output = res.error;
        else if (res?.output) {
          this.output = Array.isArray(res.output)
            ? res.output.length
              ? res.output.join("\n")
              : "Error occurred with no output"
            : res.output;
        } else if (res?.message) this.output = res.message;
        else if (error.request) {
          this.output =
            "Connection Error: Unable to reach server.\n\nCheck:\n1. Backend running\n2. /compiler/ reachable\n3. Internet stable";
        } else this.output = `Error: ${error.message}`;
      } finally {
        this.isRunning = false;
      }
    },

    async mcqCall() {
      let token = this.userInfo.token_key;
      await axios
        .get(
          `${process.env.VUE_APP_API}/course_quizzes_pk/${this.quiz_id}/?Key=${token}`,
        )
        .then((r) => {
          this.mcqset = r.data.data[0];
          console.log(this.mcqset.quiz.coursequizquestion_set);
          this.questions = r.data.data[0].quiz.coursequizquestion_set;
          this.duration = r.data.data[0].quiz.quiztakers_set;
          if (this.questions) {
            this.questions.forEach((el) => {
              el.ansclk = "";
              el.anr = false;
            });
          }
        });
    },

    handleAnswerClick(isCorrect, id) {
      this.questions[this.currentQuestion].ansclk = id;
      this.questions[this.currentQuestion].anr = isCorrect;
      let nextQuestion = this.currentQuestion + 1;

      if (nextQuestion < this.questions.length) {
        this.currentQuestion = nextQuestion;
      }
    },

    countDownTimer() {
      if (this.countDown > 0) {
        this.timer = setTimeout(() => {
          this.countDown -= 1;
          this.countDownTimer();
        }, 1000);
      }
    },

    submitMcq() {
      this.showScore = true;
      localStorage.setItem("mcqTstv", true);
      this.tst = true;
    },

    nxtQt() {
      let nextQuestion = this.currentQuestion + 1;
      if (nextQuestion < this.questions.length) {
        this.currentQuestion = nextQuestion;
      }
    },

    pvsQt() {
      if (this.currentQuestion > 0) {
        let nextQuestion = this.currentQuestion - 1;
        if (nextQuestion >= 0) {
          this.currentQuestion = nextQuestion;
        }
      }
    },

    nxtQtSubmitted() {
      if (
        this.currentAnsweredQuestion <
        this.dataval?.coursequizusersanswer_set?.length
      ) {
        this.currentAnsweredQuestion = this.currentAnsweredQuestion + 1;
      }
    },

    pvsQtSubmitted() {
      if (this.currentAnsweredQuestion > 0) {
        this.currentAnsweredQuestion = this.currentAnsweredQuestion - 1;
      }
    },

    async getSUbmittedQuiz() {
      let token = this.userInfo.token_key;

      axios
        .get(
          `${process.env.VUE_APP_API}/quiztaker__quizzes_pk/${this.quiz_id}/?Key=${token}&user__id=${this.taker_id}`,
        )
        .then((response) => {
          if (response.data.status == 200) {
            this.dataval = response.data.data;
            console.log(response.data.data, "response.data.data");
          }
          console.log("response:: test", response.data.message);
        });
    },

    cleanupQuizTimer() {
      if (this.quizInterval) {
        clearInterval(this.quizInterval);
        this.quizInterval = null;
      }
      localStorage.removeItem("quiz_time_left");
    },

    async ckans() {
      this.disable_submit = true;
      var ans_set = [];
      let token = this.userInfo.token_key;
      this.questions.forEach((el) => {
        let answerValue = null;

        if (el.ansclk !== undefined && el.ansclk !== "" && el.ansclk !== null) {
          answerValue = el.ansclk;
        }

        let descriptionValue = el.description ? el.description : null;

        let shortDescriptionValue = el.short_description
          ? el.short_description
          : null;

        let matchValue =
          el.coursequizanswer_set && el.coursequizanswer_set.length > 0
            ? el.coursequizanswer_set
            : null;

        let val = {
          question: el.id,
          answer: answerValue,
          description: descriptionValue,
          short_description: shortDescriptionValue,
          match_answer: matchValue,
        };
        if (el.choice_type === "coding" && this.codingAnswers[el.id]) {
          val.coding = this.codingAnswers[el.id].coding || "";
          val.output = this.codingAnswers[el.id].output || "";
        }
        ans_set.push(val);
      });

      this.HeaderVal = {
        quiz: this.mcqset.quiz.course,
        quiz_id:this.mcqset.quiz.id,
        quiz_title: this.mcqset.quiz.name,
        usersanswer: ans_set,
        completed: true,
      };

      try {
        const response = await axios.patch(
          `${process.env.VUE_APP_API}/course_save_multi_answer/?Key=${token}`,
          this.HeaderVal,
        );

        console.log("response", response);
        this.cleanupQuizTimer();
        this.loading = false;

        if (response.data.status == 200) {
          this.tryquizs = response.data.score;
          
          // Set quizPassed immediately based on the response
          if (this.tryquizs?.persentage == true) {
            const requiredScore = this.tryquizs.persentage_need;
            const userScore = this.tryquizs.persentage_scored;
            this.quizPassed = userScore >= requiredScore;
          } else {
            this.quizPassed = true; // No percentage requirement means passed
          }

          this.messages = {
            text: "Quiz Submitted.",
            type: "success",
          };

          setTimeout(() => {
            this.messages = {
              text: "",
              type: "",
            };
            this.disable_submit = false;
            this.quizSubmitted = true;
            this.handlePostQuizFlow();
          }, 2000);
        }
      } catch (error) {
        console.error("Error submitting quiz:", error);
        this.messages = {
          text: "Failed to submit quiz. Please try again.",
          type: "error",
        };
        this.disable_submit = false;
      }
    },
    retryQuiz() {
      console.log("Retrying quiz...");

      this.quizPassed = undefined;
      this.showFeedback = false;
      this.hasUserGivenFeedback = false;
      this.quizSubmitted = false;
      this.currentQuestion = 0;

      if (this.duration) {
        this.duration = {
          ...this.duration,
          persentage_scored: 0,
        };
      }

      this.questions.forEach((el) => {
        el.ansclk = null;
        el.anr = false;
        el.description = null;
        el.short_description = null;
      });

      this.codingAnswers = {};
      this.code = "";
      this.output = "";

      this.messages = { text: "", type: "" };

      if (this.duration?.quiz?.timer) {
        console.log(
          "Restarting timer with duration:",
          this.duration.quiz.timer,
        );

        this.cleanupQuizTimer();

        this.timeLeft = this.duration.quiz.timer * 60;

        localStorage.setItem("quiz_time_left", this.timeLeft);

        this.startQuizTimer();
      } else {
        console.log("No timer for this quiz");
      }

      window.scrollTo({ top: 0, behavior: "smooth" });

      this.messages = {
        text: "Quiz reset. Good luck!",
        type: "info",
      };

      setTimeout(() => {
        this.messages = { text: "", type: "" };
      }, 3000);
    },
  },
};
</script>

<style scoped>
.gradient-btn {
  background: #0f172a;
  --tw-shadow: 0 10px 30px -3px rgba(255, 31, 57, 0.1),
    0 10px 30px -6px rgba(255, 31, 57, 0.3);
  transition: all 0.2s linear;
  color: white;
  border: 0px;
  padding: 8px 16px;
  outline: 2px solid transparent;
  outline-offset: 2px;
  border-radius: 0.25rem;
  font-size: 1rem;
  line-height: 1.5rem;
  min-width: 160px;
  font-weight: 700;
}

/* Animation for feedback form */
.bg-gradient-to-br {
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>